import { type Division, type Team, UNASSIGNED_DIVISION } from "./types";
import { spiritStandings, type SpiritScoreRecord } from "./spirit";

/** One ceremony decision per division. A tie can have several winning teams.
 * Kept outside public settings: drafts never leave the organiser's surface. */
export interface SpiritAward {
  divisionId: string;
  teamIds: string[];
  announcedAt?: number;
  updatedAt: number;
}
export interface SpiritAwardWrite {
  teamIds: string[];
  announced: boolean;
}
export interface PublicSpiritAward {
  divisionId: string;
  teamIds: string[];
  announcedAt: number;
  /** Only announced winners' means. No per-game scores or comments. */
  averages?: { teamId: string; average: number }[];
}
export const MAX_SPIRIT_WINNERS = 20;

export interface SpiritAwardRow {
  award: PublicSpiritAward;
  team: Team;
  shared: boolean;
  average?: number;
}

/** Resolve current team names/marks, omitting removed or reassigned teams. */
export function resolveSpiritAwards(
  awards: readonly PublicSpiritAward[],
  teams: readonly Team[],
  divisions: readonly Division[] = [],
): SpiritAwardRow[] {
  const rank = (id: string): number => {
    const index = divisions.findIndex((d) => d.id === id);
    return index < 0 ? divisions.length : index;
  };
  return [...awards]
    .sort((a, b) => rank(a.divisionId) - rank(b.divisionId) || a.announcedAt - b.announcedAt)
    .flatMap((award) =>
      award.teamIds.flatMap((id) => {
        const team = teams.find(
          (t) => t.id === id && (t.divisionId ?? UNASSIGNED_DIVISION) === award.divisionId,
        );
        const average = award.averages?.find((a) => a.teamId === id)?.average;
        return team
          ? [
              {
                award,
                team,
                shared: award.teamIds.length > 1,
                ...(average !== undefined ? { average } : {}),
              },
            ]
          : [];
      }),
    );
}

export function validSpiritWinners(
  award: Pick<SpiritAward, "divisionId" | "teamIds">,
  teams: readonly Team[],
): boolean {
  return award.teamIds.every((id) =>
    teams.some(
      (team) => team.id === id && (team.divisionId ?? UNASSIGNED_DIVISION) === award.divisionId,
    ),
  );
}

/** Announcing a team award includes that winner's mean, independently of
 * publishing full standings. Never expose other teams or raw submissions. */
export function publicSpiritAwards(
  awards: readonly SpiritAward[],
  awardsPublic: boolean,
  scores?: { teams: Team[]; submissions: readonly SpiritScoreRecord[] },
): PublicSpiritAward[] {
  if (!awardsPublic) return [];
  return awards
    .flatMap((award) => {
      if (award.announcedAt === undefined || award.teamIds.length === 0) return [];
      const averages =
        scores === undefined
          ? []
          : spiritStandings({
              ...scores,
              divisionId: award.divisionId === UNASSIGNED_DIVISION ? undefined : award.divisionId,
              // Ranking does not affect a mean; award selection belongs to the organiser.
              settings: { minGames: 1, tiebreak: [] },
            })
              .filter((r) => award.teamIds.includes(r.team.id) && r.games > 0)
              .map((r) => ({ teamId: r.team.id, average: r.average }));
      return [
        {
          divisionId: award.divisionId,
          teamIds: [...award.teamIds],
          announcedAt: award.announcedAt,
          ...(averages.length > 0 ? { averages } : {}),
        },
      ];
    })
    .sort((a, b) => a.announcedAt - b.announcedAt);
}
